import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';
import { 
  Cpu, 
  HardDrive, 
  Monitor, 
  Motherboard, 
  Server, 
  Loader2, 
  RefreshCw, 
  Info,
  Zap
} from 'lucide-react';

const HardwareDetailsSection = ({ agentId }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hardwareData, setHardwareData] = useState(null);

  useEffect(() => {
    loadHardwareDetails(false); // Load cached data on mount
  }, [agentId]);

  const loadHardwareDetails = async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const res = await api.get(`/agents/${agentId}/hardware-details${refresh ? '?refresh=true' : ''}`);
      setHardwareData(res.data);
      if (refresh) {
        toast.success('Hardware details refreshed from agent');
      }
    } catch (error) {
      toast.error(refresh ? 'Failed to refresh hardware details' : 'Failed to load hardware details');
      console.error('Hardware details error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const InfoRow = ({ label, value, unit = '' }) => (
    <div className="flex justify-between py-2 border-b last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-semibold text-gray-900">
        {value || 'N/A'} {unit}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
        <p className="text-sm text-gray-600">Loading hardware details from database...</p>
      </div>
    );
  }

  if (!hardwareData) {
    return (
      <div className="text-center py-12">
        <Info className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600">No hardware details available</p>
        <Button onClick={() => loadHardwareDetails(true)} className="mt-4" variant="outline">
          Fetch from Agent
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Hardware Information</h2>
          <p className="text-xs text-gray-500 mt-1">
            Data cached from agent (updated daily)
          </p>
        </div>
        <Button 
          onClick={() => loadHardwareDetails(true)} 
          variant="outline" 
          size="sm" 
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh from Agent'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CPU Details */}
        {hardwareData.cpu && !hardwareData.cpu.error && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="w-5 h-5 text-blue-600" />
                CPU
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <InfoRow label="Model" value={hardwareData.cpu.model} />
              <InfoRow label="Manufacturer" value={hardwareData.cpu.manufacturer} />
              <InfoRow label="Clock Speed" value={hardwareData.cpu.clock_speed_mhz} unit="MHz" />
              <InfoRow label="Cores" value={hardwareData.cpu.number_of_cores} />
              <InfoRow label="Logical Processors" value={hardwareData.cpu.number_of_logical_processors} />
              <InfoRow label="L2 Cache" value={hardwareData.cpu.l2_cache_kb} unit="KB" />
              <InfoRow label="L3 Cache" value={hardwareData.cpu.l3_cache_kb} unit="KB" />
              <InfoRow label="Architecture" value={hardwareData.cpu.architecture} />
              <InfoRow label="Processor ID" value={hardwareData.cpu.processor_id} />
              <InfoRow 
                label="Status" 
                value={
                  <Badge variant={hardwareData.cpu.status === 'OK' ? 'success' : 'warning'}>
                    {hardwareData.cpu.status}
                  </Badge>
                } 
              />
            </CardContent>
          </Card>
        )}

        {/* BIOS Details */}
        {hardwareData.bios && !hardwareData.bios.error && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                BIOS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <InfoRow label="Version" value={hardwareData.bios.version} />
              <InfoRow label="SMBIOS Version" value={hardwareData.bios.smbios_version} />
              <InfoRow label="Description" value={hardwareData.bios.description} />
              <InfoRow label="Manufacturer" value={hardwareData.bios.manufacturer} />
              <InfoRow label="Release Date" value={hardwareData.bios.release_date} />
              <InfoRow label="Serial Number" value={hardwareData.bios.serial_number} />
            </CardContent>
          </Card>
        )}

        {/* Motherboard Details */}
        {hardwareData.motherboard && !hardwareData.motherboard.error && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5 text-purple-600" />
                Motherboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <InfoRow label="Manufacturer" value={hardwareData.motherboard.manufacturer} />
              <InfoRow label="Model" value={hardwareData.motherboard.model} />
              <InfoRow label="Description" value={hardwareData.motherboard.description} />
              <InfoRow label="Version" value={hardwareData.motherboard.version} />
              <InfoRow label="Serial Number" value={hardwareData.motherboard.serial_number} />
            </CardContent>
          </Card>
        )}

        {/* System Details */}
        {hardwareData.system && !hardwareData.system.error && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5 text-green-600" />
                System
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <InfoRow label="Manufacturer" value={hardwareData.system.manufacturer} />
              <InfoRow label="Model" value={hardwareData.system.model} />
              <InfoRow label="Serial Number" value={hardwareData.system.serial_number} />
              <InfoRow label="Asset Tag" value={hardwareData.system.asset_tag} />
              <InfoRow label="System Type" value={hardwareData.system.system_type} />
              <InfoRow label="Domain" value={hardwareData.system.domain} />
              <InfoRow label="Total Memory" value={hardwareData.system.total_physical_memory_gb} unit="GB" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Graphics Cards */}
      {hardwareData.graphics && Array.isArray(hardwareData.graphics) && hardwareData.graphics.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-indigo-600" />
            Graphics Cards
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {hardwareData.graphics.map((gpu, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {gpu.name || `Graphics Card ${idx + 1}`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <InfoRow label="Description" value={gpu.description} />
                  <InfoRow label="Video Processor" value={gpu.video_processor} />
                  <InfoRow label="Memory" value={gpu.adapter_ram_gb} unit="GB" />
                  <InfoRow label="Driver Version" value={gpu.driver_version} />
                  <InfoRow label="Driver Date" value={gpu.driver_date} />
                  <InfoRow 
                    label="Resolution" 
                    value={gpu.current_horizontal_resolution && gpu.current_vertical_resolution 
                      ? `${gpu.current_horizontal_resolution} x ${gpu.current_vertical_resolution}` 
                      : 'N/A'
                    } 
                  />
                  <InfoRow label="Current Refresh Rate" value={gpu.current_refresh_rate} unit="Hz" />
                  <InfoRow label="Min Refresh Rate" value={gpu.min_refresh_rate} unit="Hz" />
                  <InfoRow label="Max Refresh Rate" value={gpu.max_refresh_rate} unit="Hz" />
                  <InfoRow 
                    label="Status" 
                    value={
                      <Badge variant={gpu.status === 'OK' ? 'success' : 'warning'}>
                        {gpu.status}
                      </Badge>
                    } 
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Monitors */}
      {hardwareData.monitors && Array.isArray(hardwareData.monitors) && hardwareData.monitors.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-teal-600" />
            Connected Monitors ({hardwareData.monitors.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hardwareData.monitors.map((monitor, idx) => (
              <Card key={idx} className={monitor.is_primary ? 'border-blue-500 border-2' : ''}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Monitor {idx + 1}</span>
                    {monitor.is_primary && (
                      <Badge variant="default" className="text-xs">Primary</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <InfoRow label="Manufacturer" value={monitor.manufacturer} />
                  <InfoRow label="Model" value={monitor.model} />
                  <InfoRow label="Serial Number" value={monitor.serial_number} />
                  <InfoRow label="Resolution" value={monitor.resolution} />
                  <InfoRow label="Color Depth" value={monitor.bits_per_pixel} unit="bit" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HardwareDetailsSection;
